/**
 * <PublicAppLayout /> — sidebar + top bar for anonymous visitors browsing
 * public walls / profiles / guest tools.
 *
 * Mirrors the structure of AppLayout (so the shift from guest → signed-in
 * feels seamless) but the navigation items point to guest routes and the
 * footer swaps "Sign out" for "Log in" / "Create account" CTAs.
 */

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Heart, Target, Star, Map, Menu, X,
  PanelLeftClose, PanelLeft, AtSign, LogIn,
} from "lucide-react";
import { EvoluraLogo, EvoluraIcon } from "./EvoluraLogo";
import ReserveHandleDialog from "./ReserveHandleDialog";

const API = "/api";

const GUEST_TOOLS = [
  { to: "/swot", icon: Target, label: "Personal SWOT" },
  { to: "/values", icon: Heart, label: "Core Values" },
  { to: "/wheel", icon: Star, label: "Wheel of Life" },
  // Gratitude has no standalone guest tool — we link to a live demo wall
  // that doubles as the clearest "here's what it looks like" onboarding.
  { to: "/martin/gratitude", icon: Map, label: "Gratitude Wall" },
];

const startGoogleLogin = () => {
  const returnUrl = `${window.location.pathname}${window.location.search || ""}`;
  if (returnUrl && returnUrl !== "/") {
    sessionStorage.setItem("pending_return_url", returnUrl);
  }
  window.location.href = `${API}/auth/google/login`;
};

const PublicAppLayout = ({ children }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("public_sidebar_collapsed") === "true"
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);

  // Apply the dark "mobile-aligned" theme to every page rendered inside
  // the PublicAppLayout shell (anonymous guest tools, public profiles,
  // public walls). Landing pages don't mount this class.
  useEffect(() => {
    document.body.classList.add("app-mode");
    return () => document.body.classList.remove("app-mode");
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("public_sidebar_collapsed", String(next));
  };

  const isActive = (to) => {
    if (to === "/martin/gratitude") {
      return location.pathname.endsWith("/gratitude");
    }
    return location.pathname === to;
  };

  return (
    <div className="min-h-screen flex bg-[var(--color-canvas)]">
      {/* Mobile top bar */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-2.5"
        style={{
          background: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border-default)",
        }}
      >
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1.5 rounded-md hover:bg-black/5"
          aria-label="Open menu"
          data-testid="public-mobile-menu-btn"
        >
          <Menu size={20} />
        </button>
        <Link to="/">
          <EvoluraLogo className="h-5" variant="light" />
        </Link>
        <button
          onClick={startGoogleLogin}
          className="text-xs font-semibold px-3 py-1.5 rounded-full"
          style={{
            background: "var(--color-brand-primary)",
            color: "var(--color-text-on-brand)",
          }}
          data-testid="public-mobile-login"
        >
          Log in
        </button>
      </div>

      {/* Sidebar (drawer on mobile, sticky full-height on desktop) */}
      <aside
        className={`${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        } ${collapsed ? "md:w-16" : "md:w-64"} fixed md:sticky md:top-0 md:h-screen inset-y-0 left-0 z-50 w-64 flex-shrink-0 flex flex-col transition-all duration-200 self-start`}
        style={{
          background: "var(--color-surface)",
          borderRight: "1px solid var(--color-border-default)",
        }}
        data-testid="public-sidebar"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-4"
          style={{
            background:
              "linear-gradient(180deg, var(--color-brand-ghost) 0%, transparent 100%)",
          }}
        >
          <Link to="/" className="flex items-center gap-2">
            {collapsed ? (
              <EvoluraIcon className="h-7 w-7" />
            ) : (
              <EvoluraLogo className="h-5" variant="light" />
            )}
          </Link>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleCollapsed}
              className="p-1.5 rounded-md hover:bg-black/5 hidden md:flex"
              aria-label={collapsed ? "Expand" : "Collapse"}
              data-testid="public-sidebar-collapse"
            >
              {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
            </button>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-1.5 rounded-md hover:bg-black/5 md:hidden"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav
          className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5"
          onClick={() => setMobileOpen(false)}
        >
          {!collapsed && (
            <div
              className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "var(--color-text-placeholder)" }}
            >
              Explore tools
            </div>
          )}
          {GUEST_TOOLS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
                style={{
                  background: active ? "var(--color-brand-ghost)" : "transparent",
                  color: active
                    ? "var(--color-brand-primary)"
                    : "var(--color-text-primary)",
                  fontWeight: active ? 600 : 500,
                }}
                title={collapsed ? item.label : undefined}
                data-testid={`public-nav-${item.to.replace(/\//g, "-")}`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}

          {!collapsed && (
            <div
              className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "var(--color-text-placeholder)" }}
            >
              Get started
            </div>
          )}

          <button
            onClick={() => { setReserveOpen(true); setMobileOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-black/5"
            style={{ color: "var(--color-text-primary)", fontWeight: 500 }}
            title={collapsed ? "Reserve your handle" : undefined}
            data-testid="public-nav-reserve-handle"
          >
            <AtSign size={18} className="flex-shrink-0" style={{ color: "var(--color-brand-primary)" }} />
            {!collapsed && (
              <span className="truncate">
                Reserve your handle
                <span
                  className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle"
                  style={{
                    background: "var(--color-brand-primary)",
                    color: "var(--color-text-on-brand)",
                  }}
                >
                  NEW
                </span>
              </span>
            )}
          </button>
        </nav>

        {/* Footer — auth CTAs */}
        <div
          className="px-3 py-3 space-y-2"
          style={{ borderTop: "1px solid var(--color-border-default)" }}
        >
          <button
            onClick={startGoogleLogin}
            className="w-full flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold transition-colors"
            style={{
              background: "var(--color-brand-primary)",
              color: "var(--color-text-on-brand)",
            }}
            data-testid="public-cta-create-account"
            title={collapsed ? "Create account" : undefined}
          >
            {collapsed ? <LogIn size={16} /> : "Create account"}
          </button>
          {!collapsed && (
            <button
              onClick={startGoogleLogin}
              className="w-full text-center text-xs py-1.5 rounded-full transition-colors"
              style={{ color: "var(--color-text-secondary)" }}
              data-testid="public-cta-login"
            >
              Already have an account? Log in
            </button>
          )}
        </div>
      </aside>

      {/* Scrim for mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-12 md:pt-0">{children}</main>

      <ReserveHandleDialog
        open={reserveOpen}
        onOpenChange={setReserveOpen}
      />
    </div>
  );
};

export default PublicAppLayout;
